import React, { useEffect, useState } from 'react';
import {
  MagnifyingGlassIcon,
  ArrowDownTrayIcon,
  EyeIcon,
  CheckCircleIcon,
  XCircleIcon,
  TrashIcon,
  BuildingOfficeIcon,
  StarIcon,
  ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline';
import companiesService, {
  CompanyStats,
  CompanyReview,
  CompanyFilters,
  ReviewFilters,
} from '../services/companiesService';
import { useAuthStore } from '../stores/authStore';

interface ReviewModalProps {
  review: CompanyReview | null;
  company: CompanyStats | null;
  isOpen: boolean;
  onClose: () => void;
  onApprove: (reviewId: string) => void;
  onReject: (reviewId: string) => void;
  onDelete: (reviewId: string) => void;
}

const ReviewModal: React.FC<ReviewModalProps> = ({
  review,
  company,
  isOpen,
  onClose,
  onApprove,
  onReject,
  onDelete,
}) => {
  if (!isOpen || !review) return null;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('pt-BR');
  };

  const renderStars = (rating: number) => {
    return (
      <div className="flex items-center">
        {[1, 2, 3, 4, 5].map((star) => (
          <StarIcon
            key={star}
            className={`h-5 w-5 ${
              star <= rating ? 'text-yellow-400 fill-current' : 'text-gray-300'
            }`}
          />
        ))}
        <span className="ml-2 text-sm text-gray-600">({rating}/5)</span>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" style={{ backgroundColor: 'var(--bg-overlay)' }} onClick={onClose}></div>

        <div className="inline-block align-bottom rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full" style={{ backgroundColor: 'var(--card-bg)' }}>
          <div className="px-4 pt-5 pb-4 sm:p-6 max-h-[90vh] overflow-y-auto" style={{ backgroundColor: 'var(--card-bg)' }}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium" style={{ color: 'var(--text-primary)' }}>Detalhes da Review</h3>
              <button 
                onClick={onClose} 
                className="text-2xl leading-none transition-colors"
                style={{ color: 'var(--text-muted)' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = 'var(--text-primary)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = 'var(--text-muted)';
                }}
              >
                ×
              </button>
            </div>

            {company && (
              <div className="mb-4 p-4 rounded-lg" style={{ backgroundColor: 'var(--bg-tertiary)' }}>
                <div className="flex items-center space-x-3">
                  {company.company_avatar && (
                    <img src={company.company_avatar} alt={company.company_name} className="w-12 h-12 rounded" />
                  )}
                  <div>
                    <h4 className="font-medium" style={{ color: 'var(--text-primary)' }}>{company.company_name}</h4>
                    <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{company.company_slug}</p>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Avaliação</label>
                <div className="mt-1">{renderStars(review.overall_rating)}</div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Cultura</label>
                  <div className="mt-1">{renderStars(review.culture_rating)}</div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Salário</label>
                  <div className="mt-1">{renderStars(review.salary_rating)}</div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Crescimento</label>
                  <div className="mt-1">{renderStars(review.growth_rating)}</div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Equilíbrio Vida-Trabalho</label>
                  <div className="mt-1">{renderStars(review.work_life_rating)}</div>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-500">Recomendaria?</label>
                <p className="mt-1 text-sm text-gray-900">
                  {review.would_recommend ? (
                    <span className="inline-flex items-center text-green-600">
                      <CheckCircleIcon className="h-5 w-5 mr-1" />
                      Sim
                    </span>
                  ) : (
                    <span className="inline-flex items-center text-red-600">
                      <XCircleIcon className="h-5 w-5 mr-1" />
                      Não
                    </span>
                  )}
                </p>
              </div>

              {review.job_position && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Cargo</label>
                  <p className="mt-1 text-sm text-gray-900">{review.job_position}</p>
                </div>
              )}

              {review.time_at_company && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Tempo na Empresa</label>
                  <p className="mt-1 text-sm text-gray-900">{review.time_at_company}</p>
                </div>
              )}

              {review.comment && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Comentário</label>
                  <p className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">{review.comment}</p>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Usuário</label>
                  <p className="mt-1 text-sm text-gray-900">{review.user?.email || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Status</label>
                  <p className="mt-1">
                    {review.is_approved ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Aprovada
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                        Pendente
                      </span>
                    )}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Criada em</label>
                  <p className="mt-1 text-sm text-gray-900">{formatDate(review.created_at)}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Atualizada em</label>
                  <p className="mt-1 text-sm text-gray-900">{formatDate(review.updated_at)}</p>
                </div>
              </div>
            </div>

            <div className="mt-6 flex flex-col sm:flex-row items-stretch sm:items-center justify-end space-y-2 sm:space-y-0 sm:space-x-3 pt-4 border-t border-gray-200">
              {!review.is_approved && (
                <button
                  onClick={() => {
                    onApprove(review.id);
                    onClose();
                  }}
                  className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
                >
                  <CheckCircleIcon className="h-4 w-4 mr-2" />
                  Aprovar
                </button>
              )}
              {review.is_approved && (
                <button
                  onClick={() => {
                    onReject(review.id);
                    onClose();
                  }}
                  className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-yellow-600 hover:bg-yellow-700"
                >
                  <XCircleIcon className="h-4 w-4 mr-2" />
                  Reprovar
                </button>
              )}
              <button
                onClick={() => {
                  if (window.confirm('Tem certeza que deseja deletar esta review?')) {
                    onDelete(review.id);
                    onClose();
                  }
                }}
                className="inline-flex items-center justify-center px-4 py-2 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50"
              >
                <TrashIcon className="h-4 w-4 mr-2" />
                Deletar
              </button>
              <button
                onClick={onClose}
                className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const Companies: React.FC = () => {
  const { user: currentUser } = useAuthStore();
  const [companies, setCompanies] = useState<CompanyStats[]>([]);
  const [reviews, setReviews] = useState<CompanyReview[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<CompanyStats | null>(null);
  const [selectedReview, setSelectedReview] = useState<CompanyReview | null>(null);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingReviews, setIsLoadingReviews] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'companies' | 'reviews'>('companies');

  // Paginação empresas
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Paginação reviews
  const [reviewPage, setReviewPage] = useState(1);
  const [reviewLimit] = useState(20);
  const [reviewTotal, setReviewTotal] = useState(0);
  const [reviewTotalPages, setReviewTotalPages] = useState(0);

  // Filtros empresas
  const [search, setSearch] = useState('');
  const [minRating, setMinRating] = useState<number | undefined>();
  const [minReviews, setMinReviews] = useState<number | undefined>();

  // Filtros reviews
  const [reviewFilter, setReviewFilter] = useState<'all' | 'approved' | 'pending'>('all');

  useEffect(() => {
    if (activeTab === 'companies') {
      loadCompanies();
    } else {
      loadReviews();
    }
  }, [activeTab, page, search, minRating, minReviews, reviewPage, reviewFilter]);

  const loadCompanies = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const filters: CompanyFilters = {};
      if (search) filters.search = search;
      if (minRating !== undefined) filters.minRating = minRating;
      if (minReviews !== undefined) filters.minReviews = minReviews;

      const response = await companiesService.getCompanies(filters, { page, limit });
      setCompanies(response.companies);
      setTotal(response.total);
      setTotalPages(response.totalPages);
    } catch (err) {
      console.error('Erro ao carregar empresas:', err);
      setError('Erro ao carregar empresas. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  const loadReviews = async () => {
    try {
      setIsLoadingReviews(true);
      setError(null);

      const filters: ReviewFilters = {};
      if (reviewFilter === 'approved') filters.isApproved = true;
      if (reviewFilter === 'pending') filters.isApproved = false;

      const response = await companiesService.getAllReviews(filters, { page: reviewPage, limit: reviewLimit });
      setReviews(response.reviews);
      setReviewTotal(response.total);
      setReviewTotalPages(response.totalPages);
    } catch (err) {
      console.error('Erro ao carregar reviews:', err);
      setError('Erro ao carregar reviews. Tente novamente.');
    } finally {
      setIsLoadingReviews(false);
    }
  };

  const loadCompanyReviews = async (companyId: string) => {
    try {
      setIsLoadingReviews(true);
      const response = await companiesService.getCompanyReviews(companyId, {}, { page: 1, limit: 10 });
      setReviews(response.reviews);
      setReviewTotal(response.total);
    } catch (err) {
      console.error('Erro ao carregar reviews da empresa:', err);
    } finally {
      setIsLoadingReviews(false);
    }
  };

  const handleViewReviews = async (company: CompanyStats) => {
    setSelectedCompany(company);
    setActiveTab('reviews');
    await loadCompanyReviews(company.id);
  };

  const handleApproveReview = async (reviewId: string) => {
    if (!currentUser) return;

    const success = await companiesService.approveReview(reviewId, currentUser.id);
    if (success) {
      await loadReviews();
      if (selectedCompany) {
        await loadCompanyReviews(selectedCompany.id);
      }
    } else {
      alert('Erro ao aprovar review');
    }
  };

  const handleRejectReview = async (reviewId: string) => {
    if (!currentUser) return;

    const success = await companiesService.rejectReview(reviewId, currentUser.id);
    if (success) {
      await loadReviews();
      if (selectedCompany) {
        await loadCompanyReviews(selectedCompany.id);
      }
    } else {
      alert('Erro ao reprovar review');
    }
  };

  const handleDeleteReview = async (reviewId: string) => {
    if (!currentUser) return;

    const success = await companiesService.deleteReview(reviewId, currentUser.id);
    if (success) {
      await loadReviews();
      if (selectedCompany) {
        await loadCompanyReviews(selectedCompany.id);
      }
    } else {
      alert('Erro ao deletar review');
    }
  };

  const handleExport = async () => {
    try {
      const filters: CompanyFilters = {};
      if (search) filters.search = search;
      if (minRating !== undefined) filters.minRating = minRating;
      if (minReviews !== undefined) filters.minReviews = minReviews;

      const data = await companiesService.exportCompanies(filters);

      const headers = [
        'Nome da Empresa',
        'Slug',
        'Total de Reviews',
        'Avaliação Média',
        'Taxa de Recomendação',
        '5 Estrelas',
        '4 Estrelas',
        '3 Estrelas',
        '2 Estrelas',
        '1 Estrela',
        'Criado em',
      ];
      const rows = data.map((c) => [
        c.company_name,
        c.company_slug,
        c.total_reviews.toString(),
        c.avg_overall_rating.toFixed(2),
        (c.recommendation_rate * 100).toFixed(2) + '%',
        c.five_star_reviews.toString(),
        c.four_star_reviews.toString(),
        c.three_star_reviews.toString(),
        c.two_star_reviews.toString(),
        c.one_star_reviews.toString(),
        new Date(c.created_at).toLocaleDateString('pt-BR'),
      ]);

      const csvContent = [headers, ...rows].map((row) => row.join(',')).join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `empresas_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
    } catch (err) {
      console.error('Erro ao exportar empresas:', err);
      alert('Erro ao exportar empresas');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const renderStars = (rating: number) => {
    return (
      <div className="flex items-center">
        {[1, 2, 3, 4, 5].map((star) => (
          <StarIcon
            key={star}
            className={`h-4 w-4 ${
              star <= rating ? 'text-yellow-400 fill-current' : 'text-gray-300'
            }`}
          />
        ))}
        <span className="ml-1 text-sm text-gray-600">{rating.toFixed(1)}</span>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Empresas e Reviews</h1>
          <p className="text-gray-600">Gerenciar empresas e reviews de usuários</p>
        </div>
        {activeTab === 'companies' && (
          <button
            onClick={handleExport}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
            Exportar
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => {
              setActiveTab('companies');
              setSelectedCompany(null);
            }}
            className={`${
              activeTab === 'companies'
                ? 'border-linkedin-500 text-linkedin-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            <BuildingOfficeIcon className="h-5 w-5 inline mr-2" />
            Empresas
          </button>
          <button
            onClick={() => {
              setActiveTab('reviews');
              setSelectedCompany(null);
            }}
            className={`${
              activeTab === 'reviews'
                ? 'border-linkedin-500 text-linkedin-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            <ChatBubbleLeftRightIcon className="h-5 w-5 inline mr-2" />
            Reviews {reviewFilter === 'pending' && `(${reviewTotal})`}
          </button>
        </nav>
      </div>

      {/* Filtros */}
      {activeTab === 'companies' ? (
        <div className="card">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="md:col-span-2">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Buscar por nome ou slug..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-linkedin-500 focus:border-linkedin-500"
                />
              </div>
            </div>
            <div>
              <input
                type="number"
                placeholder="Avaliação mín."
                min="0"
                max="5"
                step="0.1"
                value={minRating || ''}
                onChange={(e) => {
                  setMinRating(e.target.value ? parseFloat(e.target.value) : undefined);
                  setPage(1);
                }}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-linkedin-500 focus:border-linkedin-500"
              />
            </div>
            <div>
              <input
                type="number"
                placeholder="Min. reviews"
                min="0"
                value={minReviews || ''}
                onChange={(e) => {
                  setMinReviews(e.target.value ? parseInt(e.target.value) : undefined);
                  setPage(1);
                }}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-linkedin-500 focus:border-linkedin-500"
              />
            </div>
          </div>
        </div>
      ) : (
        <div className="card">
          <select
            value={reviewFilter}
            onChange={(e) => {
              setReviewFilter(e.target.value as any);
              setReviewPage(1);
            }}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-linkedin-500 focus:border-linkedin-500"
          >
            <option value="all">Todas as reviews</option>
            <option value="approved">Aprovadas</option>
            <option value="pending">Pendentes</option>
          </select>
        </div>
      )}

      {/* Conteúdo */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {activeTab === 'companies' ? (
        <div className="card">
          {isLoading ? (
            <div className="text-center py-12">
              <p className="text-gray-500">Carregando empresas...</p>
            </div>
          ) : companies.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500">Nenhuma empresa encontrada</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
              <table className="min-w-full divide-y" style={{ borderColor: 'var(--border-primary)' }}>
                <thead style={{ backgroundColor: 'var(--bg-secondary)' }}>
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-primary)' }}>
                      Empresa
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-primary)' }}>
                      Avaliação
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-primary)' }}>
                      Reviews
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-primary)' }}>
                      Recomendação
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-primary)' }}>
                      Distribuição
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-primary)' }}>
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody style={{ backgroundColor: 'var(--card-bg)' }}>
                    {companies.map((company) => (
                      <tr 
                        key={company.id} 
                        className="hover:bg-gray-50"
                        style={{ 
                          borderTopColor: 'var(--border-primary)',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = 'var(--bg-tertiary)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'var(--card-bg)';
                        }}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            {company.company_avatar && (
                              <img
                                src={company.company_avatar}
                                alt={company.company_name}
                                className="w-10 h-10 rounded mr-3"
                              />
                            )}
                            <div>
                              <div className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{company.company_name}</div>
                              <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>{company.company_slug}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">{renderStars(company.avg_overall_rating)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: 'var(--text-primary)' }}>
                          {company.total_reviews}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: 'var(--text-primary)' }}>
                          {(company.recommendation_rate * 100).toFixed(1)}%
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-1 text-xs">
                            <span className="text-green-600">★{company.five_star_reviews}</span>
                            <span className="text-blue-600">★{company.four_star_reviews}</span>
                            <span className="text-yellow-600">★{company.three_star_reviews}</span>
                            <span className="text-orange-600">★{company.two_star_reviews}</span>
                            <span className="text-red-600">★{company.one_star_reviews}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button
                            onClick={() => handleViewReviews(company)}
                            className="text-linkedin-600 hover:text-linkedin-900"
                          >
                            Ver Reviews
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Paginação */}
              {totalPages > 1 && (
                <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                  <div className="flex-1 flex justify-between sm:hidden">
                    <button
                      onClick={() => setPage(Math.max(1, page - 1))}
                      disabled={page === 1}
                      className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                    >
                      Anterior
                    </button>
                    <button
                      onClick={() => setPage(Math.min(totalPages, page + 1))}
                      disabled={page === totalPages}
                      className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                    >
                      Próximo
                    </button>
                  </div>
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700">
                        Mostrando <span className="font-medium">{(page - 1) * limit + 1}</span> até{' '}
                        <span className="font-medium">{Math.min(page * limit, total)}</span> de{' '}
                        <span className="font-medium">{total}</span> resultados
                      </p>
                    </div>
                    <div>
                      <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                        <button
                          onClick={() => setPage(Math.max(1, page - 1))}
                          disabled={page === 1}
                          className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                        >
                          Anterior
                        </button>
                        {[...Array(Math.min(totalPages, 5))].map((_, i) => {
                          const pageNum = page <= 3 ? i + 1 : page - 2 + i;
                          if (pageNum > totalPages) return null;
                          return (
                            <button
                              key={pageNum}
                              onClick={() => setPage(pageNum)}
                              className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                pageNum === page
                                  ? 'z-10 bg-linkedin-50 border-linkedin-500 text-linkedin-600'
                                  : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                              }`}
                            >
                              {pageNum}
                            </button>
                          );
                        })}
                        <button
                          onClick={() => setPage(Math.min(totalPages, page + 1))}
                          disabled={page === totalPages}
                          className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                        >
                          Próximo
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      ) : (
        <div className="card">
          {selectedCompany && (
            <div className="mb-4 p-4 rounded-lg" style={{ backgroundColor: 'var(--bg-tertiary)' }}>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  {selectedCompany.company_avatar && (
                    <img
                      src={selectedCompany.company_avatar}
                      alt={selectedCompany.company_name}
                      className="w-10 h-10 rounded"
                    />
                  )}
                  <div>
                    <h3 className="font-medium" style={{ color: 'var(--text-primary)' }}>{selectedCompany.company_name}</h3>
                    <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{selectedCompany.company_slug}</p>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedCompany(null)}
                  className="text-sm transition-colors"
                  style={{ color: 'var(--text-secondary)' }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = 'var(--text-primary)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = 'var(--text-secondary)';
                  }}
                >
                  Ver todas as reviews
                </button>
              </div>
            </div>
          )}

          {isLoadingReviews ? (
            <div className="text-center py-12">
              <p className="text-gray-500">Carregando reviews...</p>
            </div>
          ) : reviews.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500">Nenhuma review encontrada</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y" style={{ borderColor: 'var(--border-primary)' }}>
                  <thead style={{ backgroundColor: 'var(--bg-secondary)' }}>
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-primary)' }}>
                        Empresa
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-primary)' }}>
                        Avaliação
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-primary)' }}>
                        Usuário
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-primary)' }}>
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-primary)' }}>
                        Data
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-primary)' }}>
                        Ações
                      </th>
                    </tr>
                  </thead>
                  <tbody style={{ backgroundColor: 'var(--card-bg)' }}>
                    {reviews.map((review) => (
                      <tr 
                        key={review.id} 
                        className="hover:bg-gray-50"
                        style={{ 
                          borderTopColor: 'var(--border-primary)',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = 'var(--bg-tertiary)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'var(--card-bg)';
                        }}
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: 'var(--text-primary)' }}>
                          {review.company?.company_name || selectedCompany?.company_name || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">{renderStars(review.overall_rating)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: 'var(--text-primary)' }}>
                          {review.user?.email || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {review.is_approved ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              Aprovada
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                              Pendente
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: 'var(--text-secondary)' }}>
                          {formatDate(review.created_at)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button
                            onClick={() => {
                              setSelectedReview(review);
                              setIsReviewModalOpen(true);
                            }}
                            className="text-linkedin-600 hover:text-linkedin-900 mr-4"
                          >
                            <EyeIcon className="h-5 w-5" />
                          </button>
                          {!review.is_approved && (
                            <button
                              onClick={() => handleApproveReview(review.id)}
                              className="text-green-600 hover:text-green-900"
                              title="Aprovar"
                            >
                              <CheckCircleIcon className="h-5 w-5" />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Paginação Reviews */}
              {reviewTotalPages > 1 && (
                <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                  <div className="flex-1 flex justify-between sm:hidden">
                    <button
                      onClick={() => setReviewPage(Math.max(1, reviewPage - 1))}
                      disabled={reviewPage === 1}
                      className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                    >
                      Anterior
                    </button>
                    <button
                      onClick={() => setReviewPage(Math.min(reviewTotalPages, reviewPage + 1))}
                      disabled={reviewPage === reviewTotalPages}
                      className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                    >
                      Próximo
                    </button>
                  </div>
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700">
                        Mostrando <span className="font-medium">{(reviewPage - 1) * reviewLimit + 1}</span> até{' '}
                        <span className="font-medium">{Math.min(reviewPage * reviewLimit, reviewTotal)}</span> de{' '}
                        <span className="font-medium">{reviewTotal}</span> resultados
                      </p>
                    </div>
                    <div>
                      <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                        <button
                          onClick={() => setReviewPage(Math.max(1, reviewPage - 1))}
                          disabled={reviewPage === 1}
                          className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                        >
                          Anterior
                        </button>
                        {[...Array(Math.min(reviewTotalPages, 5))].map((_, i) => {
                          const pageNum = reviewPage <= 3 ? i + 1 : reviewPage - 2 + i;
                          if (pageNum > reviewTotalPages) return null;
                          return (
                            <button
                              key={pageNum}
                              onClick={() => setReviewPage(pageNum)}
                              className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                pageNum === reviewPage
                                  ? 'z-10 bg-linkedin-50 border-linkedin-500 text-linkedin-600'
                                  : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                              }`}
                            >
                              {pageNum}
                            </button>
                          );
                        })}
                        <button
                          onClick={() => setReviewPage(Math.min(reviewTotalPages, reviewPage + 1))}
                          disabled={reviewPage === reviewTotalPages}
                          className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                        >
                          Próximo
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Modal de Review */}
      <ReviewModal
        review={selectedReview}
        company={selectedCompany}
        isOpen={isReviewModalOpen}
        onClose={() => {
          setIsReviewModalOpen(false);
          setSelectedReview(null);
        }}
        onApprove={handleApproveReview}
        onReject={handleRejectReview}
        onDelete={handleDeleteReview}
      />
    </div>
  );
};

